import { iotRequest } from './iot_http';

const BASE_URL = process.env.REACT_APP_BACKEND_ENDPOINT;
const PATH = '/api/iot/notifiche';

// The recipients of the IoT notifications, as contact lists per scope
// (BSDEVPLAT-97 for the anagrafica and the reparto scope, BSDEVPLAT-144 for the
// struttura scope). Every route here already exists server-side; the panel adds
// none of its own.
//
// A contatto is a recapito with a scope, not an account and not a ruolo: none of
// these calls grants access to anything, and there is no route to create a user
// from here (ADR Q13 of specs/infra-media-mobile-alerting, emended by
// BSDEVPLAT-125/144 — `responsabile_esercizio` stays the grant, `contatto_notifica`
// is the sole source of the email recipients).
//
// `iotRequest` (wrapFetchWithDetailedErrors) is used everywhere on purpose: the
// server refusals carry the sentence the screen has to show — above all the 400
// of the default reparto, whose link decides an authorisation outcome and
// therefore holds no notification contacts.

/**
 * Both scopes of one esercizio plus the coverage marker, in one read.
 * `copertoDaContattiDiStruttura` is the server's own answer to «does the
 * escalation reach anybody»: the screen shows that flag, never an inference from
 * the length of `struttura`.
 */
export const getAnagraficaContatti = (esercizioId) =>
  iotRequest(
    `${BASE_URL}${PATH}/contatti?esercizioId=${encodeURIComponent(esercizioId)}`,
    'GET',
  );

// Idempotent by contract, both ways: a second PUT of a recapito already on the
// scope answers 200 and a DELETE of one already off answers 204. That is what
// lets the screen re-read the anagrafica after every write instead of patching
// its local copy — two technicians on the same esercizio cannot make the two
// copies disagree.
export const aggiungiContattoAlReparto = (repartoId, contattoId) =>
  iotRequest(
    `${BASE_URL}${PATH}/contatti/reparto/${encodeURIComponent(repartoId)}/${encodeURIComponent(contattoId)}`,
    'PUT',
  );

export const rimuoviContattoDalReparto = (repartoId, contattoId) =>
  iotRequest(
    `${BASE_URL}${PATH}/contatti/reparto/${encodeURIComponent(repartoId)}/${encodeURIComponent(contattoId)}`,
    'DELETE',
  );

// The struttura scope hangs off the esercizio, mirroring the shape of the reparto
// pair (BSDEVPLAT-144). The two contracts diverge here and nowhere else: if the
// server route changes shape, this is the pair to change.
export const aggiungiContattoAllaStruttura = (esercizioId, contattoId) =>
  iotRequest(
    `${BASE_URL}${PATH}/contatti/struttura/${encodeURIComponent(esercizioId)}/${encodeURIComponent(contattoId)}`,
    'PUT',
  );

export const rimuoviContattoDallaStruttura = (esercizioId, contattoId) =>
  iotRequest(
    `${BASE_URL}${PATH}/contatti/struttura/${encodeURIComponent(esercizioId)}/${encodeURIComponent(contattoId)}`,
    'DELETE',
  );

/**
 * The single configuration point of the digest (`iot.digest.*`), read-only.
 * It takes no esercizioId because it is not per esercizio: changing it is an
 * environment variable and a redeploy, deliberately staged on Q8 (PO validation
 * 2026-08-06). `abilitato` is a service kill switch, not a per-esercizio toggle.
 */
export const getConfigurazioneDigest = () =>
  iotRequest(`${BASE_URL}${PATH}/digest`, 'GET');
